import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Card, Col, Form, Row, Table } from 'antd';
import { isNil } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { withNamespaces } from 'react-i18next';

import { ERROR_MESSAGE } from '../../utils/constants';
import Button from '../Button';
import FormItem from '../Form/FormItem';
import Select from '../Select';
import HorizontalSwitch from '../Switch/HorizontalSwitch';
import TextInput from '../TextInput';
import Toast from '../Toast';

/**
 * DynamicPayrollForm - A reusable form component for payroll file configurations
 *
 * @param {Object} props - Component props
 * @param {Function} props.t - Translation function
 * @param {Object} props.initialData - Initial form data
 * @param {Function} props.onSubmit - Callback when form is submitted
 * @param {Boolean} props.loading - Loading state
 * @param {Boolean} props.disabled - Disabled state
 * @param {Boolean} props.isUpdate - Is Update form
 */
const DefaultPayrollFileForm = ({
  t,
  initialData = {},
  onSubmit,
  loading = false,
  disabled = false,
  isUpdate = false,
}) => {
  const [form] = Form.useForm();
  const [hasFieldSectionError, setHasFieldSectionError] = useState(false);
  const [columnHeaders, setColumnHeaders] = useState(['']);
  const [gridData, setGridData] = useState([
    {
      key: 0,
      values: [{ label: '', value: undefined, isCustom: false }],
    },
  ]);

  const handleSubmit = useCallback(
    values => {
      const validateFieldsArray = fieldsArray => {
        let emptyLabels = [];
        let emptyValues = [];

        if (!Array.isArray(fieldsArray)) return { isValid: false };
        fieldsArray.forEach(fieldGroup => {
          if (!Array.isArray(fieldGroup)) return [];
          fieldGroup.forEach(field => {
            if (field) {
              if (!field.label || (field.label && field.label.trim() === '')) {
                emptyLabels.push(field);
              }

              if (isNil(field.value)) {
                emptyValues.push(field);
              }
            }
          });
        });

        return {
          isValid: fieldsArray.length > 0,
          hasEmptyLabels: emptyLabels.length > 0,
          hasEmptyValues: emptyValues.length > 0,
        };
      };

      const { isValid, hasEmptyLabels, hasEmptyValues } = validateFieldsArray(values.fields);

      setHasFieldSectionError(!isValid || hasEmptyLabels || hasEmptyValues);

      if (!isValid) {
        return Toast({
          type: 'error',
          message: t('fieldsSectionMustContainAtLeastOneRow'),
        });
      }

      if (hasEmptyLabels) {
        return Toast({
          type: 'error',
          message: t('allFieldMustHaveAValidLabel'),
        });
      }

      if (hasEmptyValues) {
        return Toast({
          type: 'error',
          message: t('allFieldMustHaveAValidValue'),
        });
      }

      const payload = {
        name: values.name,
        settings: {
          processingStrategy: values.processingStrategy,
        },
        fields: values.fields,
      };

      if (onSubmit) {
        onSubmit(payload);
      }
    },
    [t, onSubmit],
  );

  const convertGridToFormData = useCallback(() => {
    const fields = gridData.map(row =>
      row.values.map((field, colIndex) => ({
        ...field,
        label: columnHeaders[colIndex] || field.label,
      })),
    );
    return fields;
  }, [gridData, columnHeaders]);

  useEffect(() => {
    const fields = convertGridToFormData();
    form.setFieldValue('fields', fields);
  }, [gridData, columnHeaders, form, convertGridToFormData]);

  useEffect(() => {
    let initialColumns = [''];
    let initialGridData = [{ key: 0, values: [{ label: '', value: undefined, isCustom: false }] }];

    if (initialData.fields && Array.isArray(initialData.fields) && initialData.fields.length > 0) {
      // Extract column headers from the first row
      if (initialData.fields[0] && Array.isArray(initialData.fields[0])) {
        initialColumns = initialData.fields[0].map(field => field.label || '');
      }

      // Convert to grid format
      initialGridData = initialData.fields.map((row, rowIndex) => ({
        key: rowIndex,
        values: Array.isArray(row) ? row : [{ label: '', value: undefined, isCustom: false }],
      }));
    }

    setColumnHeaders(initialColumns);
    setGridData(initialGridData);

    const initialValues = {
      name: initialData.name || '',
      processingStrategy: initialData.settings?.processingStrategy,
      fields: convertGridToFormData(),
    };

    form.setFieldsValue(initialValues);
    // eslint-disable-next-line
  }, [form]);

  const addColumn = () => {
    setColumnHeaders([...columnHeaders, '']);
    setGridData(
      gridData.map(row => ({
        ...row,
        values: [...row.values, { label: '', value: undefined, isCustom: false }],
      })),
    );

    setTimeout(() => {
      const newFieldLabel = document.querySelector(`#fieldLabel-${columnHeaders.length}`);
      const newFieldValue = document.querySelector(
        `#fieldValue-${gridData.length - 1}-${columnHeaders.length}`,
      );
      if (newFieldLabel) {
        newFieldValue.scrollIntoView();
        newFieldLabel.focus();
      }
    }, 200);
  };

  const removeColumn = colIndex => {
    if (columnHeaders.length > 1) {
      setColumnHeaders(columnHeaders.filter((_, index) => index !== colIndex));
      setGridData(
        gridData.map(row => ({
          ...row,
          values: row.values.filter((_, index) => index !== colIndex),
        })),
      );
    }
  };

  const addRow = () => {
    const newRow = {
      key: Math.max(...gridData.map(row => row.key)) + 1,
      values: columnHeaders.map(() => ({ label: '', value: undefined, isCustom: false })),
    };
    setGridData([...gridData, newRow]);
  };

  const removeRow = rowIndex => {
    if (gridData.length > 1) {
      setGridData(gridData.filter((_, index) => index !== rowIndex));
    }
  };

  const updateColumnHeader = (colIndex, value) => {
    const newHeaders = [...columnHeaders];
    newHeaders[colIndex] = value;
    setColumnHeaders(newHeaders);
  };

  const updateGridCell = (rowIndex, colIndex, field, value) => {
    const newGridData = [...gridData];
    newGridData[rowIndex].values[colIndex] = {
      ...newGridData[rowIndex].values[colIndex],
      [field]: value,
    };
    setGridData(newGridData);
  };

  const getSelectOptions = () => [
    { value: 'accountNumber', label: t('accountNumber') },
    { value: 'comments', label: t('comments') },
    { value: 'companyPostingKey', label: t('companyPostingKey') },
    { value: 'costCenter', label: t('costCenter') },
    { value: 'currency', label: t('currency') },
    { value: 'email', label: t('email') },
    { value: 'employeeId', label: t('employee_id') },
    { value: 'exportDate', label: t('exportDate') },
    { value: 'name', label: t('name') },
    { value: 'sapCode', label: t('sapCode') },
    { value: 'totalAmount', label: t('totalAmount') },
    { value: 'totalMileage', label: t('totalMileage') },
    { value: 'userId', label: t('userId') },
    { value: 'userPostingKey', label: t('userPostingKey') },
  ];

  const columns = [
    {
      title: '',
      dataIndex: 'rowNumber',
      key: 'rowNumber',
      width: 60,
      fixed: 'left',
      render: (_, record, index) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>{index + 1}</span>
          {gridData.length > 1 && (
            <DeleteOutlined
              onClick={() => removeRow(index)}
              style={{ cursor: 'pointer', color: '#ff4d4f' }}
              disabled={disabled}
            />
          )}
        </div>
      ),
    },
    ...columnHeaders.map((header, colIndex) => ({
      title: (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <TextInput
            id={`fieldLabel-${colIndex}`}
            value={header}
            onChange={e => updateColumnHeader(colIndex, e.target.value)}
            placeholder={t('enterFieldLabel')}
            disabled={disabled}
            style={{ minWidth: 120 }}
          />
          {columnHeaders.length > 1 && (
            <DeleteOutlined
              onClick={() => removeColumn(colIndex)}
              style={{ cursor: 'pointer', color: '#ff4d4f' }}
              disabled={disabled}
            />
          )}
        </div>
      ),
      dataIndex: `col${colIndex}`,
      key: `col${colIndex}`,
      width: 300,
      render: (_, record, rowIndex) => {
        const cellData = record.values[colIndex];
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <HorizontalSwitch
                label={t('custom')}
                checked={cellData.isCustom}
                onChange={checked => {
                  updateGridCell(rowIndex, colIndex, 'isCustom', checked);
                  updateGridCell(rowIndex, colIndex, 'value', checked ? '' : undefined);
                }}
                disabled={disabled}
                size="small"
              />
            </div>
            {cellData.isCustom ? (
              <TextInput
                id={`fieldValue-${rowIndex}-${colIndex}`}
                value={cellData.value || ''}
                onChange={e => updateGridCell(rowIndex, colIndex, 'value', e.target.value)}
                placeholder={t('enterFieldValue')}
                disabled={disabled}
              />
            ) : (
              <Select
                id={`fieldValue-${rowIndex}-${colIndex}`}
                value={cellData.value}
                onChange={value => updateGridCell(rowIndex, colIndex, 'value', value)}
                placeholder={t('selectFieldValue')}
                disabled={disabled}
                options={getSelectOptions()}
                style={{ width: '100%' }}
              />
            )}
          </div>
        );
      },
    })),
  ];

  return (
    <Form form={form} onFinish={handleSubmit} layout="vertical">
      <Card title={t('payrollFileConfiguration')} style={{ marginBottom: 24 }}>
        <FormItem
          label={t('configurationName')}
          name="name"
          required
          rules={[{ required: true, message: ERROR_MESSAGE().BLANK_FIELD }]}
        >
          <TextInput placeholder={t('enterConfigurationName')} disabled={disabled} />
        </FormItem>
      </Card>

      <Card title={t('settings')} style={{ marginBottom: 24 }}>
        <FormItem
          label={t('processingStrategy')}
          name="processingStrategy"
          required
          rules={[{ required: true, message: ERROR_MESSAGE().BLANK_FIELD }]}
        >
          <Select
            disabled={disabled}
            placeholder={t('selectProcessingStrategy')}
            options={[
              { value: 'iterate', label: t('iterate') },
              { value: 'sum', label: t('sum') },
            ]}
          />
        </FormItem>
      </Card>

      <Card title={t('fields')}>
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col>
              <Button
                type="secondary"
                size="xs"
                icon={<PlusOutlined />}
                onClick={addRow}
                text={t('addRow')}
                disabled={disabled}
              />
            </Col>
            <Col>
              <Button
                type="secondary"
                size="xs"
                icon={<PlusOutlined />}
                onClick={addColumn}
                text={t('addField')}
                disabled={disabled}
              />
            </Col>
          </Row>
        </div>

        <div
          style={{
            maxHeight: '400px',
            overflow: 'auto',
            border: `1px solid ${hasFieldSectionError ? '#ff4d4f' : '#d9d9d9'}`,
            borderRadius: 6,
          }}
        >
          <FormItem name="fields" className="no-margin-bottom">
            <Table
              columns={columns}
              dataSource={gridData}
              pagination={false}
              scroll={{ y: 300 }}
              size="small"
              bordered
              style={{ marginBottom: 0 }}
            />
          </FormItem>
        </div>
      </Card>

      <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          onClick={() => form.submit()}
          text={t(isUpdate ? 'update' : 'create')}
          loading={loading}
          disabled={disabled}
        />
      </div>
    </Form>
  );
};

export default withNamespaces()(DefaultPayrollFileForm);
